import { Icon } from "@circleco/compass/components/Icon";
import { IconButton } from "@circleco/compass/components/IconButton";
import type { MenuOptions } from "@circleco/compass/components/Menu";
import { SplitIconButton } from "@circleco/compass/components/SplitIconButton";
import { Tooltip } from "@circleco/compass/components/Tooltip";

import { t } from "@/i18n-js/instance";

import { SYSTEM_DEFAULT_DEVICE_VALUE } from "../constants";
import type { MediaInputDevice } from "../hooks/mediaTypes";
import type {
  VideoClipPipPosition,
  VideoClipPipShape,
  VideoClipPipSize,
} from "../hooks/useVideoClipSettingsStore";
import { useVideoClipContext } from "../VideoClipContext";
import { LivePreview } from "./LivePreview";
import { ScreenShareSettingsPopover } from "./ScreenShareSettingsPopover";

const selectedDeviceIndicator = (
  <Icon name="checkmark" size="sm" className="shrink-0" />
);
const emptyDeviceIndicator = <span className="size-4 shrink-0" />;

const buildDeviceMenuOptions = ({
  devices,
  onSelectDevice,
  selectedDeviceId,
}: {
  devices: MediaInputDevice[];
  onSelectDevice: (deviceId: string | null) => void;
  selectedDeviceId: string | null;
}): MenuOptions => [
  {
    label: t("tiptap.video_clip.settings.default_device"),
    onClick: () => onSelectDevice(null),
    suffix:
      selectedDeviceId === null
        ? selectedDeviceIndicator
        : emptyDeviceIndicator,
  },
  ...devices
    .filter(device => device.deviceId !== SYSTEM_DEFAULT_DEVICE_VALUE)
    .map(device => ({
      label: device.label,
      onClick: () => onSelectDevice(device.deviceId),
      suffix:
        selectedDeviceId === device.deviceId
          ? selectedDeviceIndicator
          : emptyDeviceIndicator,
    })),
];

interface SetupScreenProps {
  cameraDevices: MediaInputDevice[];
  isMicEnabled: boolean;
  micDevices: MediaInputDevice[];
  onSelectCamera: (cameraId: string | null) => void;
  onSelectMic: (micId: string | null) => void;
  onSelectPipPosition: (pipPosition: VideoClipPipPosition) => void;
  onSelectPipShape: (pipShape: VideoClipPipShape) => void;
  onSelectPipSize: (pipSize: VideoClipPipSize) => void;
  onToggleCamera: () => void;
  onToggleMic: () => void;
  onToggleScreenShare: () => void;
  selectedCameraId: string | null;
  selectedMicId: string | null;
}

export const SetupScreen = ({
  cameraDevices,
  isMicEnabled,
  micDevices,
  onSelectCamera,
  onSelectMic,
  onSelectPipPosition,
  onSelectPipShape,
  onSelectPipSize,
  onToggleCamera,
  onToggleMic,
  onToggleScreenShare,
  selectedCameraId,
  selectedMicId,
}: SetupScreenProps) => {
  const { isCameraEnabled, isScreenSharing } = useVideoClipContext();

  const micMenuOptions = buildDeviceMenuOptions({
    devices: micDevices,
    onSelectDevice: onSelectMic,
    selectedDeviceId: selectedMicId,
  });

  const cameraMenuOptions = buildDeviceMenuOptions({
    devices: cameraDevices,
    onSelectDevice: onSelectCamera,
    selectedDeviceId: selectedCameraId,
  });
  const micLabel = isMicEnabled
    ? t("tiptap.video_clip.disable_mic")
    : t("tiptap.video_clip.enable_mic");
  const micOptionsLabel = t("tiptap.video_clip.settings.microphone");
  const cameraLabel = isCameraEnabled
    ? t("tiptap.video_clip.disable_camera")
    : t("tiptap.video_clip.enable_camera");
  const cameraOptionsLabel = t("tiptap.video_clip.settings.camera");
  const screenShareLabel = isScreenSharing
    ? t("tiptap.video_clip.disable_screen_share")
    : t("tiptap.video_clip.enable_screen_share");
  const screenShareOptionsLabel = t("tiptap.video_clip.settings.button");

  return (
    <div className="space-y-3">
      <LivePreview>
        <div className="absolute bottom-4 left-1/2 flex -translate-x-1/2 items-center gap-3">
          <Tooltip content={micLabel} side="bottom">
            <SplitIconButton
              icon={isMicEnabled ? "microphone" : "microphone-off"}
              aria-label={micLabel}
              align="center"
              data-testid="video-clip-mic-control"
              i18n={{
                openMenu: micOptionsLabel,
                toggleDropdown: micOptionsLabel,
              }}
              menuOptions={micMenuOptions}
              onClick={onToggleMic}
              side="bottom"
              type="button"
              variant="primary"
              size="sm"
            />
          </Tooltip>
          <Tooltip content={cameraLabel} side="bottom">
            <SplitIconButton
              icon={isCameraEnabled ? "video" : "video-off"}
              aria-label={cameraLabel}
              align="center"
              data-testid="video-clip-camera-control"
              i18n={{
                openMenu: cameraOptionsLabel,
                toggleDropdown: cameraOptionsLabel,
              }}
              menuOptions={cameraMenuOptions}
              onClick={onToggleCamera}
              side="bottom"
              type="button"
              variant="primary"
              size="sm"
            />
          </Tooltip>
          <Tooltip content={screenShareLabel} side="bottom">
            {isScreenSharing ? (
              <SplitIconButton
                icon="share-screen"
                aria-label={screenShareLabel}
                align="center"
                data-testid="video-clip-screen-share-control"
                i18n={{
                  openMenu: screenShareOptionsLabel,
                  toggleDropdown: screenShareOptionsLabel,
                }}
                onClick={onToggleScreenShare}
                popoverContent={
                  <ScreenShareSettingsPopover
                    onSelectPipPosition={onSelectPipPosition}
                    onSelectPipShape={onSelectPipShape}
                    onSelectPipSize={onSelectPipSize}
                  />
                }
                side="bottom"
                type="button"
                variant="primary"
                size="sm"
              />
            ) : (
              <IconButton
                icon="share-screen"
                aria-label={screenShareLabel}
                data-testid="video-clip-screen-share-control"
                onClick={onToggleScreenShare}
                type="button"
                variant="primary"
                size="sm"
              />
            )}
          </Tooltip>
        </div>
      </LivePreview>
    </div>
  );
};
